import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Slider } from "@mui/material";

import { TranslatedText } from "@/components/common/text";
import { InfoButton } from "@/components/common/buttons";
import { ErrorComponent } from "@/components/common/errors";
import { BestOutboundCard, NoSuggestionsContent, BestOutboundCardSkeleton, VolumeButton } from "@/components/dataPages";
import { cn } from "@/utils/css";
import { capitalizeFirstLetter, getLocationString } from "@/utils/text";
import { markToRatio, ratioToMark, useQuery } from "@/utils/query";
import { protectedRoutes } from "@/routing";
import { clearBestOutboundsState, getBestOutbounds } from "@/state/bestOutboundsSlice";
import { setCurrentSearch } from "@/state/currentSearchSlice";
import { generateMarketPotentialUrl } from "@/utils/urls";

export const PredictionLoadRatesBestOutbounds = ({ setMarketsToPin, withExtendedMarket = false }) => {
    const { trailerType: currentSearchTrailerType, delivery } = useSelector((state) => state.currentSearch);

    const [filteredOutbounds, setFilteredOutbounds] = useState(null);
    const { trailerType, deliveryZip, outboundRatio: urlOutboundRatio, pickupDate } = useQuery();
    const { data: bestOutbounds, loading, error } = useSelector((state) => state.bestOutbounds);
    const dispatch = useDispatch();

    const [ratioMark, setRatioMark] = useState(urlOutboundRatio ? ratioToMark(urlOutboundRatio) : 2);
    const [initialLoading, setInitialLoading] = useState(true);
    const timeoutRef = useRef(null);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const marks = [0, 1, 2, 3, 4].map((mark) => ({ value: mark }));

    useEffect(() => {
        dispatch(getBestOutbounds({ trailerType, zip: deliveryZip, pickupDate, useExtendedMarket: withExtendedMarket }));
    }, [trailerType, deliveryZip, pickupDate]);

    const [confidenceFilter, setConfidenceFilter] = useState(null);

    useEffect(() => {
        if (bestOutbounds) {
            console.log("Best outbounds length", bestOutbounds.length);
            let countHigh = 0;
            let countMedium = 0;
            let countLow = 0;
            bestOutbounds.forEach((outbound) => {
                if (outbound.confidence === "high") countHigh++;
                else if (outbound.confidence === "medium") countMedium++;
                else if (outbound.confidence === "low") countLow++;
            });
            console.log("Best outbounds confidence (high, medium, low):", countHigh, countMedium, countLow);
            if (countHigh > 5) {
                setConfidenceFilter({
                    high: true,
                    medium: false,
                    low: false,
                });
            } else if (countHigh + countMedium > 5) {
                setConfidenceFilter({
                    high: !!countHigh,
                    medium: true,
                    low: false,
                });
            } else {
                setConfidenceFilter({
                    high: false,
                    medium: false,
                    low: false,
                });
            }
        }
    }, [bestOutbounds]);

    useEffect(() => {
        if (confidenceFilter && bestOutbounds?.length) {
            const noConfidenceFilter = !confidenceFilter.high && !confidenceFilter.medium && !confidenceFilter.low;
            const filtered = bestOutbounds
                .filter(
                    (bo) =>
                        noConfidenceFilter ||
                        (confidenceFilter.high && bo.confidence === "high") ||
                        (confidenceFilter.medium && bo.confidence === "medium") ||
                        (confidenceFilter.low && bo.confidence === "low"),
                )
                .sort((a, b) => {
                    if (noConfidenceFilter && a.confidence !== b.confidence) {
                        if (a.confidence === "high") return -1;
                        if (b.confidence === "high") return 1;
                        if (a.confidence === "medium") return -1;
                        if (b.confidence === "medium") return 1;
                    }
                    return a.CMI[ratioMark] > b.CMI[ratioMark] ? -1 : 1;
                })
                .slice(0, 6);
            console.log("Filtered outbounds data", filtered);
            setFilteredOutbounds(filtered);
        }
    }, [bestOutbounds, confidenceFilter, ratioMark]);

    const handleRatio = (e) => {
        if (loading) return;
        setRatioMark(parseInt(e.target.value));
    };

    const handleChangedRatio = () => {
        if (loading) return;
        if (!initialLoading) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                const updatedParams = new URLSearchParams(searchParams);
                updatedParams.set("outboundRatio", `${markToRatio(ratioMark)}`);
                navigate(`${protectedRoutes[1].path}?${updatedParams.toString()}`);
            }, 500);
        } else {
            setInitialLoading(false);
        }
    };

    useEffect(() => {
        if (filteredOutbounds !== null) {
            setMarketsToPin(filteredOutbounds);
        }
    }, [filteredOutbounds]);

    useEffect(() => {
        return () => {
            dispatch(clearBestOutboundsState());
        };
    }, []);

    const handleClickToMarketPotential = () => {
        dispatch(
            setCurrentSearch({
                trailerType: currentSearchTrailerType,
                pickupDate,
                pickup: delivery,
                delivery: null,
                pickupQuery: getLocationString(delivery, false),
                deliveryQuery: "",
            }),
        );
    };

    const marketPotentialUrl = generateMarketPotentialUrl({
        trailerType: currentSearchTrailerType,
        pickup: delivery,
        pickupQuery: getLocationString(delivery, false),
        pickupDate: pickupDate,
        delivery: null,
        deliveryQuery: "",
    });

    return (
        <div className="flex flex-col lg:absolute lg:left-4 lg:top-4 bg-cm-p-surface rounded-lg w-full lg:w-[704px] h-[651px] md:h-[365px] py-5 px-6">
            <div className="text-cm-p-text-primary flex flex-col sm:flex-row sm:items-center justify-between h-[65px] sm:h-[34px]">
                <div className="flex items-center text-base leading-[25px]">
                    <div>
                        <TranslatedText>Suggested outbounds from</TranslatedText>
                        {delivery ? ` ${getLocationString(delivery, false)}` : ""}
                    </div>
                    <div className="ml-2">
                        <InfoButton text=" List of recommended destination to look into after completing the currently selected load, based on profitability." />
                    </div>
                </div>
                <div className="flex mt-3 sm:ml-2 sm:mt-0">
                    <Link
                        to={marketPotentialUrl}
                        className="border border-cm-p-outline text-cm-p-text-primary rounded-[58px] text-sm/[22px] font-medium py-[5px] px-4"
                        onClick={handleClickToMarketPotential}
                    >
                        <TranslatedText>Check market potential</TranslatedText>
                    </Link>
                </div>
            </div>
            <div className="flex flex-col md:flex-row justify-between my-5 items-start md:items-center">
                <div className="flex flex-row items-center">
                    <TranslatedText className="text-cm-p-text-primary text-sm/[22px]">Earn more on</TranslatedText>
                    <div className="h-[34px] ml-3 bg-cm-p-surface-container rounded-[48px] flex flex-row px-3 py-[6px] items-center">
                        <TranslatedText className="mr-2 text-cm-p-text-primary text-sm/[22px] font-medium">Lane</TranslatedText>
                        <div
                            className={cn("w-[138px] flex items-center", {
                                "opacity-50 cursor-not-allowed": loading,
                            })}
                        >
                            <Slider
                                value={ratioMark}
                                step={null}
                                min={0}
                                max={4}
                                marks={marks}
                                onChange={handleRatio}
                                onChangeCommitted={handleChangedRatio}
                                disabled={loading}
                            />
                        </div>
                        <TranslatedText className="ml-4 text-cm-p-text-primary text-sm/[22px] font-medium">Exit</TranslatedText>
                    </div>
                </div>
                <div className="w-[1px] h-5 bg-cm-p-outline invisible md:visible" />
                <div className="flex flex-row items-center">
                    <TranslatedText className="text-cm-p-text-primary text-sm/[22px] mr-2">Volume</TranslatedText>
                    <div className="w-full flex flex-row items-center justify-between gap-1">
                        {["high", "medium", "low"].map((confidence, index) => (
                            <VolumeButton
                                disabled={!confidenceFilter || loading}
                                key={confidence}
                                value={confidence}
                                level={3 - index}
                                active={confidenceFilter ? confidenceFilter[confidence] : false}
                                onClick={() => {
                                    setConfidenceFilter({ ...confidenceFilter, [confidence]: !confidenceFilter[confidence] });
                                }}
                            >
                                {capitalizeFirstLetter(confidence)}
                            </VolumeButton>
                        ))}
                    </div>
                </div>
            </div>
            {error ? (
                <ErrorComponent className="min-h-[452px] md:min-h-[218px]" />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {loading || filteredOutbounds === null ? (
                        [0, 1, 2, 3, 4, 5].map((index) => <BestOutboundCardSkeleton key={index} index={index} />)
                    ) : filteredOutbounds.length === 0 ? (
                        <NoSuggestionsContent className="col-span-1 md:col-span-2" />
                    ) : (
                        filteredOutbounds.map((bestOutbound, index) => (
                            <BestOutboundCard key={index} index={index} outbound={bestOutbound} isLoadRates />
                        ))
                    )}
                </div>
            )}
        </div>
    );
};
